import React, { useState } from 'react';
import { formatMontant } from '../../utils/format';
import { DollarSign, AlertTriangle, CheckCircle } from 'lucide-react';
import api from '../../services/api';
import { Button, Select } from '../../components/common/Input';
import InputMontant from '../../components/common/InputMontant';
import toast from 'react-hot-toast';

// ──────────────────────────────────────────────────────────────
// FormRegulariserEcart — solder tout ou partie d'une dette née
// d'un écart de clôture d'une tournée précise.
//
// Le vendeur peut :
//   - rapporter exactement le montant manquant → dette soldée
//   - rapporter un acompte partiel → dette passe en 'partielle'
// L'opération est tracée via remboursements_vendeur.dette_id.
// ──────────────────────────────────────────────────────────────

export default function FormRegulariserEcart({ sortie, vendeur, onSuccess, onCancel }) {
  const detteRestante = Number(sortie.dette_montant_restant || 0);
  // Pré-rempli avec le reste à régulariser pour faciliter le « tout solder »
  // d'un clic, mais entièrement effaçable si l'utilisateur veut saisir un acompte.
  const [montant, setMontant] = useState(detteRestante > 0 ? String(detteRestante) : '');
  const [modePaiement, setModePaiement] = useState('especes');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const montantNum = parseFloat(montant) || 0;
  // Le serveur plafonne déjà, mais on prévient côté UI
  const montantEffectif = Math.min(montantNum, detteRestante);
  const resteApres = detteRestante - montantEffectif;
  const soldeeApres = resteApres === 0 && montantNum > 0;
  const montantInvalide = montantNum <= 0;
  const montantDepasse = montantNum > detteRestante;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (montantInvalide) return toast.error('Montant invalide');
    setLoading(true);
    try {
      const res = await api.post(
        `/vendeurs/${vendeur.id}/dettes/${sortie.dette_id}/regulariser`,
        {
          montant: montantEffectif,
          mode_paiement: modePaiement,
          notes: notes || null,
        }
      );
      toast.success(res.data.message);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur serveur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Contexte de la dette */}
      <div className="bg-orange-900/20 border border-orange-700/50 rounded-xl p-4">
        <div className="flex items-start gap-2">
          <AlertTriangle size={18} className="text-orange-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-orange-300 text-sm font-semibold">
              Écart à régulariser — {vendeur.prenom} {vendeur.nom}
            </p>
            <p className="text-orange-200/80 text-xs mt-1">
              Tournée du {sortie.date}
              {sortie.numero_tournee > 1 && ` (#${sortie.numero_tournee})`}
            </p>
            <p className="text-orange-400 font-bold text-xl mt-2">
              {formatMontant(detteRestante)} à régulariser
            </p>
            {sortie.explication_ecart && (
              <p className="text-orange-200/70 text-xs italic mt-2">
                Motif initial : « {sortie.explication_ecart} »
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Saisie */}
      <div>
        <label className="block text-sm text-gray-400 mb-1.5">
          Montant rapporté (max {formatMontant(detteRestante)})
        </label>
        <InputMontant
          value={montant}
          onChange={setMontant}
          placeholder={String(detteRestante)}
          className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-2.5 focus:outline-none focus:border-yellow-500"
        />
      </div>

      <Select label="Mode de paiement" value={modePaiement} onChange={e => setModePaiement(e.target.value)}>
        <option value="especes">💵 Espèces</option>
        <option value="orange_money">🟠 Orange Money</option>
        <option value="mtn_money">🟡 MTN Money</option>
      </Select>

      <div>
        <label className="block text-sm text-gray-400 mb-1.5">Notes</label>
        <input
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Ex : payé le soir-même, complément reçu, etc."
          className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-500"
        />
      </div>

      {/* Aperçu */}
      {montantNum > 0 && !montantDepasse && (
        <div className={`rounded-xl p-3 ${
          soldeeApres
            ? 'bg-green-900/20 border border-green-700/30'
            : 'bg-blue-900/20 border border-blue-700/30'
        }`}>
          <div className="flex items-start gap-2">
            {soldeeApres
              ? <CheckCircle size={16} className="text-green-400 flex-shrink-0 mt-0.5" />
              : <DollarSign size={16} className="text-blue-400 flex-shrink-0 mt-0.5" />
            }
            <div className="text-xs">
              {soldeeApres ? (
                <p className="text-green-300 font-semibold">
                  ✓ Cet écart sera entièrement régularisé
                </p>
              ) : (
                <>
                  <p className="text-blue-300 font-semibold">
                    Acompte de {formatMontant(montantEffectif)}
                  </p>
                  <p className="text-blue-200/80 mt-0.5">
                    Reste à régulariser après : {formatMontant(resteApres)}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {montantDepasse && (
        <div className="bg-red-900/30 border border-red-700/60 rounded-xl p-3">
          <p className="text-red-300 text-xs">
            Le montant saisi dépasse le reste à régulariser. Il sera automatiquement
            plafonné à {formatMontant(detteRestante)}.
          </p>
        </div>
      )}

      <div className="flex space-x-3 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel} className="flex-1">
          Annuler
        </Button>
        <Button
          type="submit"
          loading={loading}
          disabled={montantInvalide}
          className="flex-1"
        >
          {soldeeApres ? 'Solder l\'écart' : 'Enregistrer l\'acompte'}
        </Button>
      </div>
    </form>
  );
}
